// Jitto dashboards, embedded with @jitto/analyze-embed (ANS-545, ANS-678).
//
// Which dashboards: src/config/jittoDashboards.json, the single map from asset
// class and dashboard to Jitto Answer's embedded-dashboard uuid. `null` is
// UNSET: the dashboard is not in Jitto Answer yet, and with the flag on the
// page shows a notice for it. src/config/jittoDashboardIds.json turns each uuid into
// Jitto's dashboard id. Neither is an access control: the backend accepts any
// dashboard id, and Jitto decides (its tenant's JITTO_EMBED_DASHBOARDS, the user's
// permission and row groups).
//
// Where the token comes from: the backend's get-jitto-embed-token function, which
// forwards the user's own ID token to Jitto. Jitto answers an opaque token and
// the frame's address on the tenant's own host; the SDK checks both against this
// environment's `jittoEmbedOrigin`, frames the dashboard and refreshes the token.
import { mountJittoDashboard as mount } from '@jitto/analyze-embed';
import jittoDashboards from '../../config/jittoDashboards.json';
import jittoDashboardIds from '../../config/jittoDashboardIds.json';
import { availableAssetClasses } from '../../common/domain/AssetClass';

export { jittoDashboards };

// The order the landing page's cards take.
const DASHBOARD_KEYS = ['summary', 'pmoverview', 'traderoverview', 'tradedetails', 'outliers'];

// Every dashboard this app embeds from Jitto Answer, for the landing page's
// list: { id, name, assetClass }, id being the route's viz_id. Built from the
// map and not asked of Jitto Answer, whose dashboard list carries no embed ids
// and answers only a signed-in Jitto user, which a TCA user is not.
export function jittoDashboardList(dashboards, map = jittoDashboards) {
  return availableAssetClasses(dashboards).flatMap((assetClass) => {
    const { ids = {}, names = {} } = dashboards[assetClass.id];
    return DASHBOARD_KEYS.filter(
      (key) => ids[key] && ids[key] !== 'na' && embeddedIdFor(ids[key], dashboards, map),
    ).map((key) => ({
      id: ids[key],
      name: `${assetClass.name} — ${names[key] || key}`,
      assetClass: assetClass.name,
    }));
  });
}

// The embedded uuid for a ThoughtSpot viz_id, or null when it is UNSET or the
// viz_id is not a configured dashboard. `dashboards` is an env config's
// `dashboards` block ({ assetClass: { ids: { key: vizId } } }).
export function embeddedIdFor(vizId, dashboards, map = jittoDashboards) {
  if (!vizId || !dashboards) return null;
  for (const [assetClass, { ids }] of Object.entries(dashboards)) {
    for (const [key, id] of Object.entries(ids || {})) {
      if (id === vizId) {
        const embedded = (map[assetClass] || {})[key];
        return embedded || null;
      }
    }
  }
  return null;
}

export function jittoEmbedIdFor(vizId, cfg) {
  if (!cfg || !cfg.jittoEmbedActiveFlag) return null;
  return embeddedIdFor(vizId, cfg.dashboards);
}

// Jitto's dashboard id for an embedded uuid (src/config/jittoDashboardIds.json), or null.
export function jittoDashboardIdFor(embedId, ids = jittoDashboardIds) {
  return (embedId && ids[embedId]) || null;
}

// The backend's refusal (a fetch Response, from authFetch) as the SDK's
// TokenRequestError: its HTTP status and Jitto's problem `code`, so the SDK can
// tell a permission failure from a network one.
async function asTokenRequestError(err) {
  if (err && typeof err.status === 'number') {
    const out = new Error(`dashboard token refused (${err.status})`);
    out.status = err.status;
    try {
      const body = await err.clone().json();
      if (body && body.code) out.code = body.code;
    } catch (ignored) {
      // No JSON body: the status alone decides.
    }
    return out;
  }
  return err instanceof Error ? err : new Error(String(err));
}

// Mount one dashboard. `requestToken(dashboardId)` resolves to the SDK's
// { token, dashboardId, embedUrl, expiresIn }. The SDK refreshes the token before
// it lapses, retries what a retry can help, and reports a failure once, through
// onFailed. `filters` are the first render's host-set filters, by filter-bar name
// (the currency seam's mount filters, undefined when the dashboard is not filtered
// by currency), applied by 0.3.0 (ANS-801). `onSizeChanged({ height })` reports the
// dashboard's content size, for a host that grows its container to fit. Returns the
// SDK's handle: setFilters, and `unmount()` when the page changes.
export function mountJittoDashboard({
  embedId,
  mountPoint,
  requestToken,
  embedOrigin,
  onFailed,
  onLoaded,
  filters,
  onSizeChanged,
  ids = jittoDashboardIds,
  mountFn = mount,
}) {
  const dashboardId = jittoDashboardIdFor(embedId, ids);
  if (!dashboardId) {
    throw new Error(`no Jitto dashboard for embedded dashboard ${embedId}`);
  }
  return mountFn(mountPoint, {
    dashboardId,
    embedOrigin,
    fetchToken: async (id) => {
      try {
        return await requestToken(id);
      } catch (err) {
        throw await asTokenRequestError(err);
      }
    },
    onFailed,
    onLoaded,
    filters,
    onSizeChanged,
  });
}
